import React from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Button, Col, Form, FormInstance, Input, Row, Select } from 'components';
import { CloseIcon, DoneIcon } from 'components/common/icons';
import { PhoneInput } from 'components/common/phoneInput';
import { ValidationErrors } from 'constants/validations';
import { VALIDATION_MESSAGE } from 'constants/validations/messages';
import { CONTACT_TYPES } from 'staticData/form';
import { ContactInfoFieldKeyType } from 'typings/form';
import { emailRegexp } from 'utils/helpers';
import { PHONE_TYPE, useContactInfoForm } from './useContactInfoForm';

const { Item } = Form;

export type DataToEditType = {
  edit_type: ContactInfoFieldKeyType;
  edit_value: string | null;
  id: string;
};

interface IContactInfoForm {
  form: FormInstance;
  onCancel: () => void;
  onSave: (id?: string) => void;
  dataToEdit?: DataToEditType;
}

export const ContactInfoForm = (props: IContactInfoForm) => {
  const { onSave, onCancel, dataToEdit, form } = props;
  const { onOptionSelect, onCancelClick, optionType, editOptionType, isChanged } = useContactInfoForm(
    form,
    onCancel,
    dataToEdit
  );
  return (
    <Row gutter={[0, 20]}>
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              name={dataToEdit ? 'edit_type' : 'type'}
              label={'Contact Type'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Field'),
                },
              ]}
            >
              <Select
                size={'large'}
                options={CONTACT_TYPES}
                showSearch
                filterOption={(input, option) => {
                  if (option) {
                    const label: string = option.label?.toString() || '';
                    return label?.toLowerCase()?.includes(input?.toLowerCase());
                  }
                  return false;
                }}
                onSelect={onOptionSelect}
              />
            </Item>
          </Col>
          <Col span={12}>
            {(() => {
              const selectedOptionType = dataToEdit ? editOptionType : optionType;
              const isPhoneInput = PHONE_TYPE.includes(selectedOptionType as string);
              const fieldName = dataToEdit ? 'edit_value' : 'value';
              return (
                <Item
                  name={fieldName}
                  label={<span style={{ display: 'none' }}></span>}
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Field'),
                    },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        if (isPhoneInput) {
                          return isValidPhoneNumber(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error(VALIDATION_MESSAGE.PHONE));
                        }

                        if (selectedOptionType === 'EMAIL' && !emailRegexp.test(value)) {
                          return Promise.reject(VALIDATION_MESSAGE.EMAIL);
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  {isPhoneInput ? <PhoneInput /> : <Input size="large" />}
                </Item>
              );
            })()}
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Row gutter={4} justify={'end'}>
          <Col>
            <Button
              disabled={!isChanged}
              color="default"
              variant="filled"
              onClick={onCancelClick}
              icon={<CloseIcon />}
            />
          </Col>
          <Col>
            <Button disabled={!isChanged} type={'primary'} icon={<DoneIcon />} onClick={() => onSave(dataToEdit?.id)} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
